/**
 * Token Universe Scanner — Vertical 7 page.
 *
 * Three views in one page:
 *   1. List — your saved scans, last-run summary, run-now button.
 *   2. Builder — single-page form to create or edit a scan (universe → criteria → scoring → schedule → review).
 *   3. Results — last run for the selected scan: universe stats, top matches with score breakdown, sampled non-matches, partial reasons, methodology hash.
 *
 * Honesty discipline (mirrors V1-V6 visual language):
 *   - Universe stats card on every result: total considered, included, excluded, exclusion breakdown, exclusionInvariantOk indicator.
 *   - Status badge: complete (green) / partial (amber, with reasons listed) / failed (red).
 *   - Methodology hash visible and hoverable (full hash on hover).
 *   - Score breakdown per match shows the actual formula used.
 *   - Empty state when 0 matches: "0 of N tokens passed all criteria — adjust thresholds."
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Compass, Plus, Trash2, RefreshCw, Play, ChevronRight, AlertCircle,
  CheckCircle, XCircle, Activity, ExternalLink, Eye, Sparkles, Hash,
} from 'lucide-react';
import {
  listScans, createScan, getScan, updateScan, deleteScan,
  runScanNow, getLatestRun,
} from '../services/api';
import TimeAgo from '../components/TimeAgo';
import { InfoTip } from '../components/ui';

const DEFAULT_NEW_SCAN = {
  name: '',
  description: '',
  universe: {
    chains: ['ethereum'],
    minLiquidityUsd: 50000,
    minAgeHours: 24,
    maxAgeHours: null,
    excludeStablecoins: true,
    excludeWrappedTokens: true,
    customExcludeAddresses: [],
  },
  criteria: [
    { type: 'VOLUME_SPIKE', criterionId: 'volume_spike_1', params: { multiplier: 5 } },
    { type: 'PRICE_CHANGE_PERCENT', criterionId: 'price_change_1', params: { timeframe: '1h', direction: 'positive', minPercent: 3, maxPercent: 50 } },
  ],
  scoring: { method: 'weighted_average', weights: {} },
  scanSchedule: { frequency: 'hourly', enabled: true },
  alerts: { enabled: false, notifyOnNewMatch: true, minScore: 0.7 },
};

const TEMPLATES = [
  {
    label: 'Volume breakouts (Solana memecoins)',
    description: 'Solana tokens with 5×+ volume spike on positive 1h price action.',
    spec: {
      ...DEFAULT_NEW_SCAN,
      name: 'Solana volume breakouts',
      universe: { ...DEFAULT_NEW_SCAN.universe, chains: ['solana'], minLiquidityUsd: 25000, minAgeHours: 24 },
      criteria: [
        { type: 'VOLUME_SPIKE', criterionId: 'volume_spike_1', params: { multiplier: 5 } },
        { type: 'PRICE_CHANGE_PERCENT', criterionId: 'price_change_1', params: { timeframe: '1h', direction: 'positive', minPercent: 3, maxPercent: 30 } },
        { type: 'LIQUIDITY_FLOOR', criterionId: 'liq_floor_1', params: { minLiquidityUsd: 25000 } },
      ],
    },
  },
  {
    label: 'Established movers (Ethereum)',
    description: 'Ethereum tokens with 24h breakout, real liquidity, and >30 days of history.',
    spec: {
      ...DEFAULT_NEW_SCAN,
      name: 'Ethereum established movers',
      universe: { ...DEFAULT_NEW_SCAN.universe, chains: ['ethereum'], minLiquidityUsd: 250000, minAgeHours: 30 * 24 },
      criteria: [
        { type: 'PRICE_BREAKOUT', criterionId: 'breakout_1', params: { breakoutAbovePct: 8 } },
        { type: 'LIQUIDITY_FLOOR', criterionId: 'liq_floor_1', params: { minLiquidityUsd: 250000 } },
        { type: 'PRICE_CHANGE_PERCENT', criterionId: 'pos_24h', params: { timeframe: '24h', direction: 'positive', minPercent: 5, maxPercent: 40 } },
      ],
    },
  },
  {
    label: 'Multi-chain new launches',
    description: 'Tokens 1–7 days old across Base + Arbitrum + Polygon with healthy liquidity.',
    spec: {
      ...DEFAULT_NEW_SCAN,
      name: 'Multi-chain new launches',
      universe: { ...DEFAULT_NEW_SCAN.universe, chains: ['base', 'arbitrum', 'polygon'], minLiquidityUsd: 50000, minAgeHours: 24, maxAgeHours: 7 * 24 },
      criteria: [
        { type: 'AGE_RANGE', criterionId: 'age_1', params: { minHours: 24, maxHours: 7 * 24 } },
        { type: 'LIQUIDITY_FLOOR', criterionId: 'liq_floor_1', params: { minLiquidityUsd: 50000 } },
        { type: 'VOLUME_SPIKE', criterionId: 'vol_spike_1', params: { multiplier: 3 } },
      ],
    },
  },
];

function PolicyBanner() {
  return (
    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 text-sm text-gray-300 flex items-start gap-2 mb-4">
      <Eye className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="text-emerald-300 font-medium">Public methodology — verifiable without an account</div>
        <p className="text-xs text-gray-400 mt-0.5">
          Every scan run discloses universe stats, partial-run reasons, data freshness, and a SHA-256 methodology hash.
          Read the full ToS at{' '}
          <a href="/api/scanner/policy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 font-mono">
            /api/scanner/policy
          </a>.
        </p>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = status === 'complete' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
    : status === 'partial' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
    : status === 'failed'  ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
    : 'bg-zinc-700/40 text-zinc-300 border-zinc-600';
  const label = status || 'unknown';
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>{label}</span>;
}

// ─── List view ───────────────────────────────────────────────────────────────

function ScanList({ scans, onCreate, onSelect, onDelete, onRunNow, runningId }) {
  return (
    <div className="space-y-4">
      <PolicyBanner />

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-200">Your scans ({scans.length})</h2>
        <button onClick={() => onCreate(null)} className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg px-3 py-1.5 flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Create scan
        </button>
      </div>

      {scans.length === 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
          <Compass className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
          <p className="text-sm text-zinc-300">No scans yet. Start from a template or build your own.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {scans.map(scan => (
          <div key={scan.scanId} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-zinc-100 truncate">{scan.name}</h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {scan.universe.chains.join(', ')} · {scan.criteria.length} criteria · {scan.scanSchedule.frequency}
                </p>
              </div>
              {scan.lastRunStatus && <StatusBadge status={scan.lastRunStatus} />}
            </div>
            <div className="text-xs text-zinc-500 space-y-0.5 mb-3">
              <div>Last run: {scan.lastRunAt ? <TimeAgo timestamp={scan.lastRunAt} /> : <span>never</span>}</div>
              <div>Total runs: {scan.totalRuns || 0} · Total matches found: {scan.totalMatchesFound || 0}</div>
              {scan.lastRunMatchCount != null && <div>Last run matches: <span className="text-zinc-300">{scan.lastRunMatchCount}</span></div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => onSelect(scan.scanId, 'results')}
                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs rounded px-2 py-1.5 flex items-center justify-center gap-1">
                <Activity className="w-3 h-3" /> Results
              </button>
              <button onClick={() => onRunNow(scan.scanId)} disabled={runningId === scan.scanId}
                className="flex-1 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs rounded px-2 py-1.5 flex items-center justify-center gap-1">
                {runningId === scan.scanId ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Run now
              </button>
              <button onClick={() => onCreate(scan)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs rounded px-2 py-1.5">Edit</button>
              <button onClick={() => onDelete(scan.scanId)} className="text-zinc-500 hover:text-rose-400 px-2"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-zinc-200 mt-6 mb-3">Templates — clone & customize</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TEMPLATES.map(t => (
            <button key={t.label} onClick={() => onCreate({ ...t.spec })}
              className="text-left bg-zinc-900/50 hover:bg-zinc-900 border border-zinc-800 hover:border-emerald-500/30 rounded-xl p-4 transition-colors">
              <Sparkles className="w-4 h-4 text-emerald-400 mb-2" />
              <div className="text-sm text-zinc-100 font-medium">{t.label}</div>
              <div className="text-xs text-zinc-500 mt-1">{t.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Builder view ────────────────────────────────────────────────────────────

const SUPPORTED_CHAINS = ['ethereum', 'solana', 'base', 'bsc', 'polygon', 'arbitrum'];
const SUPPORTED_CRITERION_TYPES = ['VOLUME_SPIKE', 'PRICE_CHANGE_PERCENT', 'LIQUIDITY_FLOOR', 'PRICE_BREAKOUT', 'AGE_RANGE'];
const SCORING_METHODS = ['weighted_average', 'minimum', 'all_or_nothing'];
const FREQUENCIES = ['every_15_minutes', 'hourly', 'every_6_hours', 'daily'];

function CriterionEditor({ criterion, onChange, onRemove }) {
  function setParam(k, v) { onChange({ ...criterion, params: { ...criterion.params, [k]: v } }); }
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <select value={criterion.type} onChange={e => onChange({ ...criterion, type: e.target.value, params: {} })}
          className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100">
          {SUPPORTED_CRITERION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={criterion.criterionId || ''} onChange={e => onChange({ ...criterion, criterionId: e.target.value })}
          placeholder="ID for scoring weights"
          className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-300 font-mono" />
        <button onClick={onRemove} className="text-zinc-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
      </div>
      {criterion.type === 'VOLUME_SPIKE' && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Multiplier (1h vs 24h-avg)</span>
            <input type="number" step="0.5" min="1" value={criterion.params.multiplier ?? 5}
              onChange={e => setParam('multiplier', Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100" />
          </label>
        </div>
      )}
      {criterion.type === 'PRICE_CHANGE_PERCENT' && (
        <div className="grid grid-cols-4 gap-2">
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Timeframe</span>
            <select value={criterion.params.timeframe || '1h'} onChange={e => setParam('timeframe', e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100">
              <option value="1h">1h</option><option value="24h">24h</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Direction</span>
            <select value={criterion.params.direction || 'positive'} onChange={e => setParam('direction', e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100">
              <option value="positive">positive</option>
              <option value="negative">negative</option>
              <option value="either">either</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Min %</span>
            <input type="number" step="0.5" value={criterion.params.minPercent ?? 3}
              onChange={e => setParam('minPercent', Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100" />
          </label>
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Max %</span>
            <input type="number" step="0.5" value={criterion.params.maxPercent ?? 50}
              onChange={e => setParam('maxPercent', Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100" />
          </label>
        </div>
      )}
      {criterion.type === 'LIQUIDITY_FLOOR' && (
        <label className="block">
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Min liquidity (USD)</span>
          <input type="number" step="1000" min="0" value={criterion.params.minLiquidityUsd ?? 50000}
            onChange={e => setParam('minLiquidityUsd', Number(e.target.value))}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
      )}
      {criterion.type === 'PRICE_BREAKOUT' && (
        <label className="block">
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Breakout above 24h-start (%)</span>
          <input type="number" step="0.5" min="0" value={criterion.params.breakoutAbovePct ?? 5}
            onChange={e => setParam('breakoutAbovePct', Number(e.target.value))}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100" />
        </label>
      )}
      {criterion.type === 'AGE_RANGE' && (
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Min age (hours)</span>
            <input type="number" step="1" min="0" value={criterion.params.minHours ?? 24}
              onChange={e => setParam('minHours', Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100" />
          </label>
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Max age (hours, blank = ∞)</span>
            <input type="number" step="1" min="0" value={criterion.params.maxHours ?? ''}
              onChange={e => setParam('maxHours', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100" />
          </label>
        </div>
      )}
    </div>
  );
}

function ScanBuilder({ initial, editingScanId, onSaved, onCancel }) {
  const [draft, setDraft] = useState(() => initial || DEFAULT_NEW_SCAN);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function setUniv(k, v) { setDraft(d => ({ ...d, universe: { ...d.universe, [k]: v } })); }
  function toggleChain(c) {
    setDraft(d => {
      const has = d.universe.chains.includes(c);
      const chains = has ? d.universe.chains.filter(x => x !== c) : [...d.universe.chains, c];
      return { ...d, universe: { ...d.universe, chains } };
    });
  }
  function addCriterion() {
    setDraft(d => ({ ...d, criteria: [...d.criteria, { type: 'LIQUIDITY_FLOOR', criterionId: `liq_floor_${d.criteria.length + 1}`, params: { minLiquidityUsd: 50000 } }] }));
  }
  function updateCriterion(i, c) {
    setDraft(d => ({ ...d, criteria: d.criteria.map((x, idx) => idx === i ? c : x) }));
  }
  function removeCriterion(i) {
    setDraft(d => ({ ...d, criteria: d.criteria.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const payload = {
        name: draft.name,
        description: draft.description || null,
        universe: draft.universe,
        criteria: draft.criteria,
        scoring: draft.scoring,
        scanSchedule: draft.scanSchedule,
        alerts: draft.alerts,
      };
      const r = editingScanId
        ? await updateScan(editingScanId, payload)
        : await createScan(payload);
      onSaved(r?.data || r);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const universeWeights = useMemo(() => {
    const ids = (draft.criteria || []).map(c => c.criterionId).filter(Boolean);
    const w = {};
    for (const id of ids) {
      w[id] = draft.scoring.weights?.[id] ?? Number((1 / ids.length).toFixed(3));
    }
    return w;
  }, [draft.criteria, draft.scoring.weights]);

  return (
    <div className="space-y-5">
      <PolicyBanner />

      {/* Step 1 — Identity */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-3">Step 1 — Identity</h3>
        <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          placeholder="Scan name (e.g. 'Solana volume breakouts')"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 mb-2" />
        <textarea rows={2} value={draft.description || ''} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
          placeholder="Optional description"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-xs text-zinc-300" />
      </div>

      {/* Step 2 — Universe */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-3">Step 2 — Universe</h3>
        <div className="mb-3">
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Chains</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {SUPPORTED_CHAINS.map(c => (
              <button key={c} onClick={() => toggleChain(c)}
                className={`text-xs px-2.5 py-1 rounded border ${draft.universe.chains.includes(c) ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:text-zinc-200'}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider inline-flex items-center gap-1">
              Min liquidity (USD)
              <InfoTip side="top">25k for memecoins, 50k for general filter, 250k+ for established movers. Lower = more rug noise. Higher = miss new launches.</InfoTip>
            </span>
            <input type="number" step="1000" value={draft.universe.minLiquidityUsd ?? ''}
              onChange={e => setUniv('minLiquidityUsd', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100" />
          </label>
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider inline-flex items-center gap-1">
              Min age (hours)
              <InfoTip side="top">24h excludes brand-new rugs that disappear within hours. 720 (30 days) excludes most pump-and-dumps. Adjust based on what kind of tokens you want to find.</InfoTip>
            </span>
            <input type="number" step="1" value={draft.universe.minAgeHours ?? ''}
              onChange={e => setUniv('minAgeHours', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100" />
          </label>
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Max age (hours, blank = ∞)</span>
            <input type="number" step="1" value={draft.universe.maxAgeHours ?? ''}
              onChange={e => setUniv('maxAgeHours', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100" />
          </label>
        </div>
        <div className="flex gap-4 text-xs">
          <label className="flex items-center gap-2 text-zinc-300">
            <input type="checkbox" checked={!!draft.universe.excludeStablecoins}
              onChange={e => setUniv('excludeStablecoins', e.target.checked)} />
            Exclude stablecoins
          </label>
          <label className="flex items-center gap-2 text-zinc-300">
            <input type="checkbox" checked={!!draft.universe.excludeWrappedTokens}
              onChange={e => setUniv('excludeWrappedTokens', e.target.checked)} />
            Exclude wrapped tokens (WETH, WBTC, stETH…)
          </label>
        </div>
      </div>

      {/* Step 3 — Criteria */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-zinc-200">Step 3 — Criteria (AND logic, max 6)</h3>
          <button onClick={addCriterion} disabled={draft.criteria.length >= 6}
            className="text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-200 rounded px-2 py-1 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="space-y-2">
          {draft.criteria.map((c, i) => (
            <CriterionEditor key={i} criterion={c} onChange={v => updateCriterion(i, v)} onRemove={() => removeCriterion(i)} />
          ))}
        </div>
      </div>

      {/* Step 4 — Scoring */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-3">Step 4 — Scoring</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider inline-flex items-center gap-1">
              Method
              <InfoTip side="right">weighted_average = combine criteria scores by weights (ranks matches). minimum = score is the lowest criterion (penalizes weakest signal). all_or_nothing = 1 if all matched else 0 (flat pass/fail).</InfoTip>
            </span>
            <select value={draft.scoring.method} onChange={e => setDraft(d => ({ ...d, scoring: { ...d.scoring, method: e.target.value } }))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100">
              {SCORING_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
        </div>
        {draft.scoring.method === 'weighted_average' && (
          <div className="space-y-1.5">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Weights (will be normalized to sum=1)</span>
            {Object.entries(universeWeights).map(([id, w]) => (
              <div key={id} className="flex items-center gap-2 text-xs">
                <span className="text-zinc-400 font-mono w-44 truncate">{id}</span>
                <input type="number" step="0.05" min="0" max="1" value={draft.scoring.weights?.[id] ?? w}
                  onChange={e => setDraft(d => ({ ...d, scoring: { ...d.scoring, weights: { ...(d.scoring.weights || {}), [id]: Number(e.target.value) } } }))}
                  className="w-24 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-100" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Step 5 — Schedule + Alerts */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-200 mb-3">Step 5 — Schedule + Alerts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <label className="block">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider inline-flex items-center gap-1">
              Frequency
              <InfoTip side="top">Worker runs every 15 min — your scan fires at the next boundary AFTER frequency elapsed. Expect ±15 min jitter, not exact-on-the-hour.</InfoTip>
            </span>
            <select value={draft.scanSchedule.frequency} onChange={e => setDraft(d => ({ ...d, scanSchedule: { ...d.scanSchedule, frequency: e.target.value } }))}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100">
              {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 mt-5">
            <input type="checkbox" checked={!!draft.scanSchedule.enabled}
              onChange={e => setDraft(d => ({ ...d, scanSchedule: { ...d.scanSchedule, enabled: e.target.checked } }))} />
            Enabled
          </label>
        </div>
        <div className="flex flex-wrap gap-4 text-xs">
          <label className="flex items-center gap-2 text-zinc-300">
            <input type="checkbox" checked={!!draft.alerts.enabled}
              onChange={e => setDraft(d => ({ ...d, alerts: { ...d.alerts, enabled: e.target.checked } }))} />
            In-app alerts on new matches
          </label>
          <label className="flex items-center gap-2 text-zinc-400">
            Min score
            <input type="number" step="0.05" min="0" max="1" value={draft.alerts.minScore ?? 0.7}
              onChange={e => setDraft(d => ({ ...d, alerts: { ...d.alerts, minScore: Number(e.target.value) } }))}
              className="w-20 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-100" />
          </label>
          <span className="text-[10px] text-zinc-500 self-center">v1: in-app only — email/Telegram/webhook deferred to v2</span>
        </div>
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-rose-200 text-sm flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={save} disabled={saving || !draft.name || draft.universe.chains.length === 0 || draft.criteria.length === 0}
          className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm rounded-lg px-4 py-2">
          {saving ? 'Saving…' : (editingScanId ? 'Save changes' : 'Create scan')}
        </button>
        <button onClick={onCancel} className="text-zinc-400 hover:text-white text-sm rounded-lg px-4 py-2">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Results view ────────────────────────────────────────────────────────────

function UniverseStatsCard({ stats }) {
  if (!stats) return null;
  const reasons = Object.entries(stats.exclusionReasons || {}).filter(([, v]) => v > 0);
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
      <h4 className="text-sm font-semibold text-zinc-200 mb-2">Universe stats</h4>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <Stat label="Considered" value={stats.totalTokensConsidered} />
        <Stat label="Included" value={stats.tokensIncludedInScan} tone="good" />
        <Stat label="Excluded" value={stats.tokensExcluded} tone="warn" />
      </div>
      {reasons.length > 0 && (
        <div className="text-xs text-zinc-400 space-y-0.5">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Exclusions by reason</div>
          {reasons.map(([k, v]) => (
            <div key={k} className="flex justify-between"><span className="font-mono">{k}</span><span>{v}</span></div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-zinc-500 mt-3">
        Invariant {stats.exclusionInvariantOk ? '✓' : '✗'} included + excluded === considered.{' '}
        Scope: {stats.universeScopeNote}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = 'default', hint }) {
  const cls = tone === 'good' ? 'text-emerald-300' : tone === 'warn' ? 'text-amber-300' : 'text-zinc-100';
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-2.5">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${cls}`}>{value ?? '—'}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function MatchCard({ match }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">#{match.rank}</span>
            <h4 className="text-sm font-semibold text-zinc-100">{match.tokenSymbol || match.tokenAddress.slice(0, 8)}</h4>
            {match.tokenName && <span className="text-xs text-zinc-500 truncate">{match.tokenName}</span>}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{match.chain} · {match.tokenAddress.slice(0, 10)}…{match.tokenAddress.slice(-6)}</div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-emerald-400">{(match.score * 100).toFixed(0)}%</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">match</div>
        </div>
      </div>

      {/* Score breakdown */}
      <div className="space-y-1 mt-2">
        {Object.entries(match.scoreBreakdown || {}).map(([id, r]) => (
          <div key={id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-[11px]">
            <div className="flex items-center justify-between">
              <span className="font-mono text-zinc-400">{id}</span>
              <span className="text-zinc-300">
                score {(r.score * 100).toFixed(0)}%
                {r.contribution != null && <span className="text-emerald-400 ml-2">+{(r.contribution * 100).toFixed(1)} contrib</span>}
              </span>
            </div>
            {r.methodology && <div className="text-zinc-500 mt-0.5">{r.methodology}</div>}
            {r.status === 'pending' && (
              <div className="text-amber-400 mt-0.5">⚠ pending — {r.reason}</div>
            )}
          </div>
        ))}
      </div>

      {/* Token snapshot */}
      {match.tokenSnapshot && (
        <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
          <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
            <div className="text-zinc-500">Price</div>
            <div className="text-zinc-100 font-mono">${match.tokenSnapshot.priceUsd?.toFixed(6) ?? '—'}</div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
            <div className="text-zinc-500">Liquidity</div>
            <div className="text-zinc-100 font-mono">${(match.tokenSnapshot.liquidityUsd ?? 0).toLocaleString()}</div>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
            <div className="text-zinc-500">24h vol</div>
            <div className="text-zinc-100 font-mono">${(match.tokenSnapshot.volume24h ?? 0).toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Caveats */}
      {match.caveats?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {match.caveats.map(c => (
            <span key={c} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">
              ⚠ {c.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      <div className="flex gap-3 mt-3 text-xs">
        {match.tokenSnapshot?.dexUrl && (
          <a href={match.tokenSnapshot.dexUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
            DexScreener <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <span className="text-zinc-500 ml-auto italic">Match score is a signal, not a recommendation.</span>
      </div>
    </div>
  );
}

function ScanResults({ scanId, onBack, onEdit }) {
  const [scan, setScan] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const [s, r] = await Promise.all([getScan(scanId), getLatestRun(scanId)]);
      setScan(s?.data || s);
      const inner = r?.data || r;
      setRun(inner?.status === 'no_runs' ? null : inner);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scanId]);

  async function handleRunNow() {
    setRunning(true);
    try {
      const r = await runScanNow(scanId);
      setRun(r?.data || r);
      // Pull updated scan metadata
      const s = await getScan(scanId);
      setScan(s?.data || s);
    } catch (e) { setError(e.message); }
    finally { setRunning(false); }
  }

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!scan) return <div className="text-sm text-zinc-500">Scan not found.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="text-xs text-zinc-400 hover:text-white">← Back to scans</button>
        <span className="text-zinc-700">/</span>
        <button onClick={() => onEdit(scan)} className="text-xs text-zinc-400 hover:text-white">Edit definition</button>
      </div>

      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-zinc-100">{scan.name}</h2>
        {run && <StatusBadge status={run.status} />}
        <button onClick={handleRunNow} disabled={running}
          className="ml-auto bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm rounded-lg px-3 py-1.5 flex items-center gap-1.5">
          {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Running…' : 'Run now'}
        </button>
      </div>

      {error && <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-rose-200 text-sm">{error}</div>}

      {!run && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
          <Compass className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
          <p className="text-sm text-zinc-300">No runs yet. Click <span className="text-emerald-300">Run now</span> to execute this scan.</p>
        </div>
      )}

      {run && (
        <>
          {run.status === 'partial' && run.partialReasons?.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-amber-300 font-medium">Run was partial — read the reasons before acting on results</div>
                <ul className="text-xs text-amber-200/90 mt-1 list-disc list-inside space-y-0.5">
                  {run.partialReasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <UniverseStatsCard stats={run.universeStats} />
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-zinc-200 mb-2">Run summary</h4>
              <div className="text-xs text-zinc-400 space-y-1">
                <div>Status: <StatusBadge status={run.status} /></div>
                <div>Started: <TimeAgo timestamp={run.startedAt} /></div>
                <div>Completed: <TimeAgo timestamp={run.completedAt} /></div>
                <div>Duration: {run.durationMs}ms</div>
                <div>Matches: <span className="text-emerald-300 font-medium">{run.matchCount}</span></div>
                {run.errors?.length > 0 && <div>Evaluator errors: <span className="text-rose-300">{run.errors.length}</span></div>}
              </div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-zinc-200 mb-2 flex items-center gap-1">
                <Hash className="w-3.5 h-3.5 text-zinc-500" /> Methodology hash
              </h4>
              <code className="block text-[10px] text-zinc-400 font-mono break-all" title={run.methodologyHash}>
                {run.methodologyHash}
              </code>
              <div className="text-[10px] text-zinc-500 mt-2">
                Reproducible — same universe + criteria + scoring ⇒ same hash.
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-zinc-200 mb-3">
              Top matches ({run.matchCount === 0 ? 'none' : `${Math.min(run.topMatches?.length || 0, run.matchCount)} of ${run.matchCount}`})
            </h3>
            {run.matchCount === 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center text-sm text-zinc-400">
                <CheckCircle className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                <p>0 of {run.universeStats?.tokensIncludedInScan ?? '?'} in-scope tokens passed all criteria.</p>
                <p className="text-xs text-zinc-500 mt-1">Adjust thresholds or scope to find matches.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {run.topMatches?.map(m => <MatchCard key={m.tokenAddress + m.rank} match={m} />)}
              </div>
            )}
          </div>

          {run.nonMatches?.sampleNonMatches?.length > 0 && (
            <details className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3">
              <summary className="text-xs text-zinc-400 cursor-pointer">
                Non-matches sample ({run.nonMatches.count} total — showing {run.nonMatches.sampleNonMatches.length} random)
              </summary>
              <div className="mt-2 space-y-1 text-[11px]">
                {run.nonMatches.sampleNonMatches.map((nm, i) => (
                  <div key={i} className="flex justify-between text-zinc-500">
                    <span className="font-mono">{nm.tokenSymbol} ({nm.chain})</span>
                    <span>failed: {nm.failedCriterion || '—'}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}

// ─── Page shell ──────────────────────────────────────────────────────────────

export default function ScannerPage() {
  const [scans, setScans] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'builder' | 'results'
  const [selectedScanId, setSelectedScanId] = useState(null);
  const [editingScan, setEditingScan] = useState(null); // null when creating, scan obj when editing
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningId, setRunningId] = useState(null);

  async function refreshScans() {
    setLoading(true); setError(null);
    try {
      const r = await listScans();
      const inner = r?.data || r;
      setScans(inner?.scans || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { refreshScans(); }, []);

  function startCreate(template) {
    if (template && template.scanId) {
      // Editing existing
      setEditingScan(template);
    } else if (template) {
      // From a template spec
      setEditingScan({ ...template, scanId: undefined });
    } else {
      setEditingScan({ ...DEFAULT_NEW_SCAN });
    }
    setView('builder');
  }

  async function handleSaved(scan) {
    await refreshScans();
    setSelectedScanId(scan.scanId);
    setView('results');
  }

  async function handleRunNow(scanId) {
    setRunningId(scanId);
    try {
      await runScanNow(scanId);
      await refreshScans();
      setSelectedScanId(scanId);
      setView('results');
    } catch (e) { setError(e.message); }
    finally { setRunningId(null); }
  }

  async function handleDelete(scanId) {
    if (!window.confirm('Delete this scan permanently? Run history will also be deleted.')) return;
    try {
      await deleteScan(scanId);
      await refreshScans();
    } catch (e) { setError(e.message); }
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-100 mb-1 flex items-center gap-2">
          <Compass className="w-6 h-6 text-emerald-400" />
          Token Universe Scanner
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 ml-2">
            VERTICAL 7
          </span>
        </h1>
        <p className="text-sm text-zinc-400">
          Define criteria. We surface matching tokens across whole chains with full universe stats, methodology hash, and explicit empty / partial states.
        </p>
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-rose-200 text-sm mb-4">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : view === 'list' ? (
        <ScanList
          scans={scans}
          onCreate={startCreate}
          onSelect={(id) => { setSelectedScanId(id); setView('results'); }}
          onDelete={handleDelete}
          onRunNow={handleRunNow}
          runningId={runningId}
        />
      ) : view === 'builder' ? (
        <ScanBuilder
          initial={editingScan}
          editingScanId={editingScan?.scanId}
          onSaved={handleSaved}
          onCancel={() => setView('list')}
        />
      ) : (
        <ScanResults
          scanId={selectedScanId}
          onBack={() => setView('list')}
          onEdit={(scan) => { setEditingScan(scan); setView('builder'); }}
        />
      )}
    </div>
  );
}
